import FileApi from 'api/FileApi';
import cx from 'classnames';
import { saveAs } from 'file-saver';
import { useGetUserFullName } from 'hooks/useGetUserFullName';
import { useUserProfile } from 'hooks/useUserProfile';
import { isNil } from 'lodash';
import { DateTime, Interval } from 'luxon';
import { useGetApprovalAttachments } from 'pages/approvals/hooks/useGetApprovalAttachments';
import { FileIcon } from 'pages/task_team/views/checks/TaskFileAttachments';
import { CheckCircleFill, ExclamationTriangleFill, QuestionCircleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

/**
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 */
export function ApprovalItemMeta({ approvalItem }) {
    const { t, i18n } = useTranslation();
    const userProfile = useUserProfile();
    const createdBy = useGetUserFullName(approvalItem.createdBy, false);

    const createdAt = DateTime.fromISO(approvalItem.createdAt).setLocale(i18n.language);

    return (
        <>
            <div className="meta-row d-flex">
                <div className="meta-label" style={{ color: '#64748b', width: '38%' }}>
                    {t('ai:approvalItemMeta.label.requestedBy')}
                </div>
                <div className="meta-value font-weight-bolder" style={{ width: '62%' }}>
                    {createdBy}
                    <div className="font-weight-normal small" style={{ color: '#64748b' }}>
                        {createdAt.toLocaleString(DateTime.DATETIME_MED)}
                    </div>
                </div>
            </div>

            <div className="meta-row d-flex mt-2">
                <div className="meta-label" style={{ color: '#64748b', width: '38%' }}>
                    {t('ai:approvalItemMeta.label.identifiers')}
                </div>
                <div className="meta-value font-weight-bolder" style={{ width: '62%' }}>
                    {t('ai:approvalItemStatus.col.sequence')} #{approvalItem.sequence}
                </div>
            </div>

            {!isNil(approvalItem.version?.versionNumber) && (
                <div className="meta-row d-flex mt-2">
                    <div className="meta-label" style={{ color: '#64748b', width: '38%' }}>
                        {t('ai:approvalItemMeta.label.version')}
                    </div>
                    <div className="meta-value font-weight-bolder" style={{ width: '62%' }}>
                        {t('ai:approvalItemStatus.versionPrefix')} {approvalItem.version.versionNumber}
                    </div>
                </div>
            )}

            <div className="meta-row d-flex mt-2">
                <div className="meta-label" style={{ color: '#64748b', width: '38%' }}>
                    {t('ai:approvalItemMeta.label.deadline')}
                </div>
                <div className="meta-value" style={{ width: '62%' }}>
                    <Deadline dateTimeString={approvalItem.deadline} />
                </div>
            </div>

            {approvalItem.createdBy === userProfile.userId && (
                <div className="meta-row d-flex mt-2">
                    <div className="meta-label" style={{ color: '#64748b', width: '38%' }}>
                        {t('ai:approvalItemMeta.label.reminder')}
                    </div>
                    <div className="meta-value" style={{ width: '62%' }}>
                        <RenderReminder approvalItem={approvalItem} />
                    </div>
                </div>
            )}

            {approvalItem.description && (
                <div
                    className="bg-white p-2 mt-3"
                    style={{ borderLeft: '3px solid #94a3b8', borderRadius: '0 4px 4px 0' }}
                >
                    <div
                        className="small text-uppercase font-weight-bolder mb-1"
                        style={{ color: '#64748b', fontSize: 11 }}
                    >
                        {t('ai:approvalItemMeta.label.description')}
                    </div>
                    <div className="font-italic" style={{ color: '#475569' }}>
                        &#34;{approvalItem.description}&#34;
                    </div>
                </div>
            )}

            {approvalItem.attachments.length > 0 && (
                <div className="mt-3">
                    <div className="meta-label mb-1" style={{ color: '#64748b', width: '38%' }}>
                        {t('ai:approvalItemMeta.label.attachments')}
                    </div>
                    <RenderAttachments approvalItem={approvalItem} />
                </div>
            )}
        </>
    );
}

/**
 * @param {string} dateTimeString
 */
function Deadline({ dateTimeString }) {
    const { t, i18n } = useTranslation();

    if (!dateTimeString) {
        return <div className="text-muted">{t('ai:deadline.noDeadline')}</div>;
    }

    const deadline = DateTime.fromISO(dateTimeString).setLocale(i18n.language);
    const deadlineFull = deadline.toLocaleString(DateTime.DATETIME_MED);

    const showDeadlineDanger = isShowDeadlineDanger();
    const showDeadlineWarning = !showDeadlineDanger && isShowDeadlineWarning();

    return (
        <div className="font-weight-bolder">
            {deadlineFull}
            <div className="flex-center font-weight-normal small pt-1" style={{ color: '#64748b' }}>
                {(showDeadlineDanger || showDeadlineWarning) && (
                    <ExclamationTriangleFill
                        size={12}
                        className={cx('flex-shrink-0 mr-1', {
                            'text-warning': showDeadlineWarning,
                            'text-danger': showDeadlineDanger,
                        })}
                    />
                )}
                {deadline.toRelative()}
            </div>
        </div>
    );

    function isShowDeadlineWarning() {
        const now = DateTime.now();

        if (deadline < now) {
            return true;
        }

        const i = Interval.fromDateTimes(now, deadline);

        return i.length('days') < 8;
    }

    function isShowDeadlineDanger() {
        const now = DateTime.now();

        if (deadline <= now) {
            return true;
        }

        const i = Interval.fromDateTimes(now, deadline);

        return i.length('days') <= 1;
    }
}

/**
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 */
function RenderReminder({ approvalItem }) {
    const { t, i18n } = useTranslation();

    if (!approvalItem.reminderDate) {
        return <div className="text-muted">{t('ai:approvalItemMeta.noReminder')}</div>;
    }

    const reminder = DateTime.fromISO(approvalItem.reminderDate).setLocale(i18n.language);
    const reminderFull = reminder.toLocaleString(DateTime.DATETIME_MED);

    return (
        <div className="d-flex">
            <div>{reminderFull}</div>

            {approvalItem.remindersProcessed ? (
                <div className="flex-center text-success small ml-3">
                    <CheckCircleFill className="mr-1" />
                    {t('ai:approvalItemMeta.sent')}
                </div>
            ) : (
                <div className="flex-center text-muted small ml-3">
                    <QuestionCircleFill className="mr-1" />
                    {t('ai:approvalItemMeta.notSent')}
                </div>
            )}
        </div>
    );
}

/**
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 */
function RenderAttachments({ approvalItem }) {
    const { items } = useGetApprovalAttachments(approvalItem['@id']);

    return (
        <>
            {items.map((attachment) => (
                <div key={`attachment-${attachment.id}`}>
                    <div className="d-flex align-items-center mb-2">
                        <FileIcon mime={attachment.mimeType} />

                        <a href="#" onClick={() => downloadFile(attachment)}>
                            {attachment.fileName}
                        </a>
                    </div>
                </div>
            ))}
        </>
    );

    /**
     * @param {import('models/approval.models').ApprovalAttachment} file
     */
    function downloadFile(file) {
        FileApi.getPrivateFile(file.uri)
            .then((response) => {
                saveAs(response.data, response.headers['x-suggested-filename'] ?? file.fileName);
            })
            .catch(() => {});
    }
}
